import angular from 'angular';
import template from './templates/time-picker.tpl.html';

const timepickerConfig = {
  MINUTE_STEP: 15,
  HOUR_STEP: 1,
  MERIDIAN_ON: 'hh:mm a',
  MERIDIAN_OFF: 'HH:mm',
  TIME_MERIDIAN_REGEX: /^(0?[0-9]|1[0-2]):[0-5][0-9] ?[a|p]m$/i,
  TIME_REGEX: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
  APM_REGEX: /[a|p]m/i
};

class TimepickerController {

  static get $inject() {
    return ['$scope', '$document', '$parse', '$animate', '$filter', '$attrs', '$element',
            '$timeout'];
  }

  constructor($scope, $document, $parse, $animate, $filter, $attrs, $element, $timeout) {
    this.$scope = $scope;
    this.$document = $document;
    this.$parse = $parse;
    this.$animate = $animate;
    this.$filter = $filter;
    this.$attrs = $attrs;
    this.$element = $element;
    this.$timeout = $timeout;
  }

  $onInit() {
    this.isOpen = false;
    this.$document.on('click', angular.bind(this, this.onClickAway));
    this.$scope.$on('$destroy', () => this.$document.off('click', this.onClickAway));

    this.disableMinutes = this.disableMinutes === true || this.$attrs.disableMinutes === 'disabled';

    this.minuteStep = this.minuteStep ? this.minuteStep : timepickerConfig.MINUTE_STEP;
    this.hourStep = this.hourStep ? this.hourStep : timepickerConfig.HOUR_STEP;

    this.showMeridian = this.showMeridian !== false &&
      this.$scope.$eval(this.$attrs.showMeridian) !== false;

    const defaultPlaceholder = this.showMeridian ?
      timepickerConfig.MERIDIAN_ON : timepickerConfig.MERIDIAN_OFF.toLowerCase();
    this.placeholder = this.$attrs.placeholder ? this.$attrs.placeholder : defaultPlaceholder;

    this.$element.find('input').on('keypress', (e) => {
      const k = e.which || e.keyCode,
        isMeridian = angular.element(this).attr('name') === 'meridian';

      //to prevent enter key to close dropdown and
      //still making meridian button functional
      if (k === 13 && !isMeridian) {
        this.preventDefaultEvents(e);
      }
    });
    this.dropdownElement = angular.element(this.$element[0].querySelector('.dropdown-menu'));
    this.dropdownElement.on('click', (e) => this.preventDefaultEvents(e));
    this.$scope.$on('$destroy', () => this.dropdownElement.off('click'));
    this.$scope.$watch(() => {
      return this.showMeridian;
    }, this.handleMeridianSwitch.bind(this));

    this.$animate.enabled(this.$element, false);

    this.ngModel.$parsers.push(this.parseViewTime.bind(this));
    this.ngModel.$parsers.push((viewValue) => {
      if (!viewValue) {
        return undefined;
      }

      this.timepickerValue = new Date(viewValue.getTime());

      if (this.timezone) {
        const timeZoneGivenOffset = this.timezoneToOffset(this.timezone);
        const localeTimeZoneOffset = viewValue.getTimezoneOffset();
        viewValue.setMinutes(viewValue.getMinutes() - (localeTimeZoneOffset - timeZoneGivenOffset));
      }
      return new Date(viewValue.getTime());
    });

    this.ngModel.$formatters.push(() => this.generateDisplayTime(this.ngModel.$modelValue));

    this.ngModel.$render = () => {
      this.inputTime = this.ngModel.$viewValue;
      const gmtDate = this.parseViewTime(this.ngModel.$viewValue);
      if (gmtDate) {
        this.timepickerValue = new Date(gmtDate.getTime());
      }
    };
  }

  handleMeridianSwitch() {
    this.inputTime = this.generateDisplayTime(this.ngModel.$modelValue);
    this.timeChanged();
  }

  // Code taken from
  // https://github.com/angular-ui/bootstrap/blob/master/src/dateparser/dateparser.js#L495
  timezoneToOffset(timezone, fallback) {
    timezone = timezone.replace(/:/g, '');
    const requestedTimezoneOffset = Date.parse(`Jan 01, 1970 00:00:00 ${timezone}`) / 60000;
    return isNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
  }

  generateDisplayTime(value) {
    const timeFormat = this.showMeridian ? timepickerConfig.MERIDIAN_ON :
      timepickerConfig.MERIDIAN_OFF;
    return this.$filter('date')(value, timeFormat, this.timezone || undefined);
  }

  onClickAway(e) {
    if (this.isOpen && !this.$element[0].contains(e.target)) {
      this.$scope.$apply(() => {
        this.isOpen = false;
      });
    }
  }

  parseViewTime(value) {
    const date = new Date();

    if (value == null) {
      return undefined;
    }

    //date is always valid
    if (angular.isDate(value)) {
      return value;
    }

    if (!angular.isString(value)) {
      return undefined;
    }

    const timeRegex = this.showMeridian ? timepickerConfig.TIME_MERIDIAN_REGEX :
      timepickerConfig.TIME_REGEX;

    if (!timeRegex.test(value)) {
      return undefined;
    }

    const [hoursValue, minutesValue] = this.parse(value);
    date.setHours(hoursValue, minutesValue);

    if (this.disableMinutes && minutesValue > 0) {
      return undefined;
    }

    return date;
  }

  isMinuteDisabled() {
    return this.disableMinutes === true || this.$scope.$eval(this.disableMinutes) === true;
  }

  toggle() {
    if (this.isDisabled || this.isReadonly) {
      return;
    }
    if (!this.isOpen && !angular.isDate(this.ngModel.$modelValue)) {
      this.inputTime = this.generateDisplayTime(new Date());
      this.timeChanged();
    }
    this.isOpen = !this.isOpen;
  }

  preventDefaultEvents(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  timeChanged() {
    this.ngModel.$setViewValue(this.inputTime);
  }

  parse(value) {
    const sp = value.split(':'),
      apm = sp[1].match(timepickerConfig.APM_REGEX);

    if (apm) {
      let hr = parseInt(sp[0], 10);
      if (hr === 12) {
        hr = 0;
      }
      sp[1] = sp[1].replace(timepickerConfig.APM_REGEX, '');
      if (apm[0].toLowerCase() === 'pm') {
        sp[0] = hr + 12;
      } else if (sp[0] === '12') {
        sp[0] = '0';
      }
    }
    return sp;
  }

  timepickerValueChanged() {
    const timeFormat = this.showMeridian ? timepickerConfig.MERIDIAN_ON :
      timepickerConfig.MERIDIAN_OFF;
    this.inputTime = this.$filter('date')(this.timepickerValue, timeFormat);
    this.timeChanged();
  }
}

export default () => {
  return {
    restrict: 'E',
    transclude: false,
    scope: {},
    require: {
      ngModel: ''
    },
    bindToController: {
      timezone: '@',
      showMeridian: '=?',
      isDisabled: '=?',
      isReadonly: '=?',
      disableMinutes: '=?isMinuteDisabled',
      hourStep: '=?',
      minuteStep: '=?'
    },
    controller: TimepickerController,
    controllerAs: 'timepicker',
    template
  };
};
